import React, { useEffect, useState } from 'react';
import { DatabaseSettingsModel } from '../../services/DatabaseSettings';

// Deliberately loose — this is an admin typing addresses they already have, so
// the check is here to catch typos, not to validate deliverability.
const isPlausibleEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

function EmailWhitelist() {
    // null until the first snapshot arrives.
    const [emails, setEmails] = useState<string[] | null>(null);
    const [enabled, setEnabled] = useState(false);
    const [newEmail, setNewEmail] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        return DatabaseSettingsModel.subscribeSettings(
            (settings) => {
                setEmails(settings.whitelistedEmails);
                setEnabled(settings.whitelistEnabled);
            },
            (err) => setError(`Failed to load settings: ${err.message}`),
        );
    }, []);

    const handleToggle = async (value: boolean) => {
        setBusy(true);
        setError('');
        try {
            await DatabaseSettingsModel.setWhitelistEnabled(value);
        } catch (err: any) {
            setError(err.message || 'Failed to update the whitelist switch');
        } finally {
            setBusy(false);
        }
    };

    const handleAdd = async (event: React.FormEvent) => {
        event.preventDefault();

        const candidate = newEmail.trim().toLowerCase();
        if (!isPlausibleEmail(candidate)) {
            setError('Enter a valid email address.');
            return;
        }
        if (emails?.includes(candidate)) {
            setError(`${candidate} is already on the list.`);
            return;
        }

        setBusy(true);
        setError('');
        try {
            await DatabaseSettingsModel.addWhitelistedEmail(candidate);
            setNewEmail('');
        } catch (err: any) {
            setError(err.message || 'Failed to add the address');
        } finally {
            setBusy(false);
        }
    };

    const handleRemove = async (email: string) => {
        setBusy(true);
        setError('');
        try {
            await DatabaseSettingsModel.removeWhitelistedEmail(email);
        } catch (err: any) {
            setError(err.message || 'Failed to remove the address');
        } finally {
            setBusy(false);
        }
    };

    if (!emails) {
        return <p className="text-gray-500">Loading…</p>;
    }

    const sortedEmails = [...emails].sort((a, b) => a.localeCompare(b));

    return (
        <div>
            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                    {error}
                </div>
            )}

            <div className="flex items-start gap-3 mb-6 p-4 bg-gray-50 rounded border border-gray-200">
                <input
                    type="checkbox"
                    id="whitelist-enabled"
                    checked={enabled}
                    onChange={(e) => handleToggle(e.target.checked)}
                    disabled={busy}
                    className="mt-1 h-4 w-4"
                />
                <label htmlFor="whitelist-enabled" className="text-sm">
                    <span className="font-medium text-gray-900">Restrict access to whitelisted emails</span>
                    <span className="block text-gray-600 mt-1">
                        When off, every verified account can sign in and the list below is
                        ignored (but kept). Admin accounts always have access either way —
                        they don't need to be on the list.
                    </span>
                </label>
            </div>

            <form onSubmit={handleAdd} className="flex gap-2 mb-6">
                <input
                    type="email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    placeholder="tester@example.com"
                    className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                    type="submit"
                    disabled={busy || !newEmail.trim()}
                    className="bg-blue-500 text-white px-4 py-2 rounded text-sm hover:bg-blue-600 disabled:opacity-50"
                >
                    Add
                </button>
            </form>

            {sortedEmails.length === 0 ? (
                <p className="text-gray-500 text-sm">
                    No addresses whitelisted yet.
                    {enabled && ' With the restriction on, only admins can sign in.'}
                </p>
            ) : (
                <ul className="divide-y divide-gray-200 border border-gray-200 rounded">
                    {sortedEmails.map((email) => (
                        <li key={email} className="flex items-center justify-between px-4 py-2">
                            <span className="text-sm text-gray-800">{email}</span>
                            <button
                                onClick={() => handleRemove(email)}
                                disabled={busy}
                                className="text-red-600 text-sm hover:text-red-800 disabled:opacity-50"
                            >
                                Remove
                            </button>
                        </li>
                    ))}
                </ul>
            )}

            <p className="text-gray-500 text-xs mt-4">
                {sortedEmails.length} address{sortedEmails.length === 1 ? '' : 'es'} whitelisted.
                Removing one doesn't end a session that's already signed in — it takes
                effect the next time that account signs in.
            </p>
        </div>
    );
}

export default EmailWhitelist;
